import {
  AlertTriangle,
  Check,
  Clock,
  DollarSign,
  Edit2,
  ExternalLink,
  Loader2,
  Lock,
  LogOut,
  Mail,
  Package,
  Plus,
  RefreshCw,
  Settings,
  Star,
  Tag,
  Trash2,
  TrendingUp,
  X,
  XCircle,
} from 'lucide-react';
import type React from 'react';
import { useEffect, useState } from 'react';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import {
  adminCancelOrder,
  adminDeleteOrder,
  adminSetPrice,
  fetchOrders,
  updateOrderStatus,
} from '../services/apiService';
import type { Order, OrderStatus, PromoCode } from '../types';

interface SpinOption {
  id: number;
  label: string;
  prizeType: string;
  prizeValue: number;
  probability: number;
  color: string;
  enabled: boolean;
}

interface PopularModelAdmin {
  id: number;
  name: string;
  imageKey: string;
  imageUrl: string;
  modelUrl: string;
  price: number;
  sortOrder: number;
  enabled: boolean;
}

interface FilamentColor {
  id: number;
  name: string;
  hexCode: string;
  inStock: boolean;
}

const ADMIN_TOKEN_KEY = 'printy_admin_token';
const ADMIN_TOKEN_EXPIRY_KEY = 'printy_admin_token_expiry';

const AdminDashboard: React.FC = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);
  const [password, setPassword] = useState('');
  const [authError, setAuthError] = useState<string | null>(null);

  // Spin wheel state
  const [spinOptions, setSpinOptions] = useState<SpinOption[]>([]);
  const [spinLoading, setSpinLoading] = useState(false);
  const [showSpinConfig, setShowSpinConfig] = useState(false);
  const [editingOption, setEditingOption] = useState<SpinOption | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);

  // Filament colors state
  const [colors, setColors] = useState<FilamentColor[]>([]);
  const [colorsLoading, setColorsLoading] = useState(false);
  const [showColorsConfig, setShowColorsConfig] = useState(false);
  const [editingColor, setEditingColor] = useState<FilamentColor | null>(null);
  const [showColorModal, setShowColorModal] = useState(false);

  // Popular models state
  const [popularModels, setPopularModels] = useState<PopularModelAdmin[]>([]);
  const [popularLoading, setPopularLoading] = useState(false);
  const [showPopularConfig, setShowPopularConfig] = useState(false);
  const [editingModel, setEditingModel] = useState<PopularModelAdmin | null>(null);
  const [showModelModal, setShowModelModal] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);

  // Promo codes state
  const [promoCodes, setPromoCodes] = useState<PromoCode[]>([]);
  const [promoLoading, setPromoLoading] = useState(false);
  const [showPromoConfig, setShowPromoConfig] = useState(false);
  const [editingPromo, setEditingPromo] = useState<PromoCode | null>(null);
  const [showPromoModal, setShowPromoModal] = useState(false);

  // Price editing state
  const [editingPriceId, setEditingPriceId] = useState<number | null>(null);
  const [editingPriceValue, setEditingPriceValue] = useState('');

  // Check for existing admin token on mount
  useEffect(() => {
    const token = localStorage.getItem(ADMIN_TOKEN_KEY);
    const expiry = localStorage.getItem(ADMIN_TOKEN_EXPIRY_KEY);

    if (token && expiry && Date.now() < Number.parseInt(expiry)) {
      setIsAuthenticated(true);
    } else {
      localStorage.removeItem(ADMIN_TOKEN_KEY);
      localStorage.removeItem(ADMIN_TOKEN_EXPIRY_KEY);
    }
    setAuthLoading(false);
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      loadOrders();
      loadSpinOptions();
      loadColors();
      loadPopularModels();
      loadPromoCodes();
    }
  }, [isAuthenticated]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthLoading(true);
    setAuthError(null);

    try {
      const response = await fetch('/api/admin/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Authentication failed');
      }

      localStorage.setItem(ADMIN_TOKEN_KEY, data.token);
      localStorage.setItem(ADMIN_TOKEN_EXPIRY_KEY, String(Date.now() + data.expiresIn));
      setIsAuthenticated(true);
      setPassword('');
    } catch (err) {
      setAuthError(err instanceof Error ? err.message : 'Authentication failed');
    } finally {
      setAuthLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem(ADMIN_TOKEN_KEY);
    localStorage.removeItem(ADMIN_TOKEN_EXPIRY_KEY);
    setIsAuthenticated(false);
  };

  const loadOrders = async () => {
    setLoading(true);
    const data = await fetchOrders();
    setOrders(data);
    setLoading(false);
  };

  const handleStatusUpdate = async (id: number, newStatus: OrderStatus) => {
    await updateOrderStatus(id, newStatus);
    setOrders(orders.map((o) => (o.id === id ? { ...o, status: newStatus } : o)));
  };

  // Spin wheel functions
  const loadSpinOptions = async () => {
    setSpinLoading(true);
    try {
      const response = await fetch('/api/admin/spin-options');
      const data = await response.json();
      setSpinOptions(data.options || []);
    } catch (error) {
      console.error('Failed to load spin options:', error);
    } finally {
      setSpinLoading(false);
    }
  };

  const handleSaveOption = async (option: SpinOption) => {
    try {
      const url = option.id ? `/api/admin/spin-options/${option.id}` : '/api/admin/spin-options';
      const method = option.id ? 'PUT' : 'POST';
      await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(option),
      });
      await loadSpinOptions();
      setEditingOption(null);
      setShowAddModal(false);
    } catch (error) {
      console.error('Failed to save spin option:', error);
    }
  };

  const handleDeleteOption = async (id: number) => {
    if (!confirm('Delete this spin option?')) return;
    try {
      await fetch(`/api/admin/spin-options/${id}`, { method: 'DELETE' });
      await loadSpinOptions();
    } catch (error) {
      console.error('Failed to delete spin option:', error);
    }
  };

  const handleToggleOption = async (option: SpinOption) => {
    await handleSaveOption({ ...option, enabled: !option.enabled });
  };

  // Filament colors functions
  const loadColors = async () => {
    setColorsLoading(true);
    try {
      const response = await fetch('/api/admin/colors');
      const data = await response.json();
      setColors(data.colors || []);
    } catch (error) {
      console.error('Failed to load colors:', error);
    } finally {
      setColorsLoading(false);
    }
  };

  const handleSaveColor = async (color: FilamentColor) => {
    try {
      const url = color.id ? `/api/admin/colors/${color.id}` : '/api/admin/colors';
      const method = color.id ? 'PUT' : 'POST';
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(color),
      });
      if (!response.ok) {
        const data = await response.json();
        alert(data.error || 'Failed to save color');
        return;
      }
      await loadColors();
      setEditingColor(null);
      setShowColorModal(false);
    } catch (error) {
      console.error('Failed to save color:', error);
    }
  };

  const handleDeleteColor = async (id: number) => {
    if (!confirm('Delete this color?')) return;
    try {
      const response = await fetch(`/api/admin/colors/${id}`, { method: 'DELETE' });
      if (!response.ok) {
        const data = await response.json();
        alert(data.error || 'Failed to delete color');
        return;
      }
      await loadColors();
    } catch (error) {
      console.error('Failed to delete color:', error);
    }
  };

  const handleToggleColorStock = async (color: FilamentColor) => {
    await handleSaveColor({ ...color, inStock: !color.inStock });
  };

  // Popular models functions
  const loadPopularModels = async () => {
    setPopularLoading(true);
    try {
      const response = await fetch('/api/admin/popular-models');
      const data = await response.json();
      setPopularModels(data.models || []);
    } catch (error) {
      console.error('Failed to load popular models:', error);
    } finally {
      setPopularLoading(false);
    }
  };

  const handleUploadImage = async (file: File): Promise<string | null> => {
    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('image', file);
      const res = await fetch('/api/admin/upload-image', { method: 'POST', body: formData });
      if (!res.ok) {
        const data = await res.json();
        alert(data.error || 'Upload failed');
        return null;
      }
      const data = await res.json();
      return data.key;
    } catch (error) {
      console.error('Image upload error:', error);
      alert('Failed to upload image');
      return null;
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSaveModel = async (model: PopularModelAdmin) => {
    try {
      const url = model.id ? `/api/admin/popular-models/${model.id}` : '/api/admin/popular-models';
      const method = model.id ? 'PUT' : 'POST';
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(model),
      });
      if (!response.ok) {
        const data = await response.json();
        alert(data.error || 'Failed to save model');
        return;
      }
      await loadPopularModels();
      setEditingModel(null);
      setShowModelModal(false);
    } catch (error) {
      console.error('Failed to save popular model:', error);
    }
  };

  const handleDeleteModel = async (id: number) => {
    if (!confirm('Delete this popular model?')) return;
    try {
      const response = await fetch(`/api/admin/popular-models/${id}`, { method: 'DELETE' });
      if (!response.ok) {
        const data = await response.json();
        alert(data.error || 'Failed to delete model');
        return;
      }
      await loadPopularModels();
    } catch (error) {
      console.error('Failed to delete popular model:', error);
    }
  };

  const handleToggleModelEnabled = async (model: PopularModelAdmin) => {
    await handleSaveModel({ ...model, enabled: !model.enabled });
  };

  // Promo code functions
  const loadPromoCodes = async () => {
    const token = localStorage.getItem(ADMIN_TOKEN_KEY);
    setPromoLoading(true);
    try {
      const response = await fetch('/api/admin/promo-codes', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        console.error('Failed to load promo codes:', response.status, data);
        alert(
          (data as { error?: string }).error || `Failed to load promo codes (${response.status})`
        );
        return;
      }
      const data = await response.json();
      setPromoCodes(data.promoCodes || []);
    } catch (error) {
      console.error('Failed to load promo codes:', error);
      alert('Failed to load promo codes. Please refresh and try again.');
    } finally {
      setPromoLoading(false);
    }
  };

  const handleSavePromo = async (promo: PromoCode) => {
    const token = localStorage.getItem(ADMIN_TOKEN_KEY);
    try {
      const url = promo.id ? `/api/admin/promo-codes/${promo.id}` : '/api/admin/promo-codes';
      const method = promo.id ? 'PUT' : 'POST';
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(promo),
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        alert((data as { error?: string }).error || 'Failed to save promo code');
        return;
      }
      await loadPromoCodes();
      setEditingPromo(null);
      setShowPromoModal(false);
    } catch (error) {
      console.error('Failed to save promo code:', error);
      alert('Failed to save promo code. Please check your connection and try again.');
    }
  };

  const handleDeletePromo = async (id: number) => {
    if (!confirm('Delete this promo code?')) return;
    const token = localStorage.getItem(ADMIN_TOKEN_KEY);
    try {
      const response = await fetch(`/api/admin/promo-codes/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        alert(
          (data as { error?: string }).error || `Failed to delete promo code (${response.status})`
        );
        return;
      }
      await loadPromoCodes();
    } catch (error) {
      console.error('Failed to delete promo code:', error);
      alert('Failed to delete promo code. Please check your connection and try again.');
    }
  };

  const handleTogglePromo = async (promo: PromoCode) => {
    await handleSavePromo({ ...promo, enabled: !promo.enabled });
  };

  const handleSetPrice = async (orderId: number) => {
    const priceNum = Number.parseFloat(editingPriceValue);
    if (isNaN(priceNum) || priceNum <= 0) {
      alert('Please enter a valid price');
      return;
    }
    try {
      await adminSetPrice(orderId, priceNum);
      setOrders(
        orders.map((o) =>
          o.id === orderId ? { ...o, price: priceNum, status: 'priced' as OrderStatus } : o
        )
      );
      setEditingPriceId(null);
      setEditingPriceValue('');
    } catch (error) {
      alert(error instanceof Error ? error.message : 'Failed to set price');
    }
  };

  const getStatusBadgeClass = (status: OrderStatus) => {
    switch (status) {
      case 'pending':
        return 'badge-pending';
      case 'priced':
        return 'badge-priced';
      case 'confirmed':
        return 'badge-confirmed';
      case 'printing':
        return 'badge-printing';
      case 'completed':
        return 'badge-completed';
      case 'ready':
        return 'badge-ready';
      case 'cancelled':
        return 'badge-cancelled';
      default:
        return 'badge-pending';
    }
  };

  const handleDeleteOrder = async (orderId: number) => {
    if (!confirm('Permanently delete this cancelled order? This cannot be undone.')) return;
    try {
      await adminDeleteOrder(orderId);
      setOrders(orders.filter((o) => o.id !== orderId));
    } catch (error) {
      alert(error instanceof Error ? error.message : 'Failed to delete order');
    }
  };

  const handleAdminCancel = async (orderId: number) => {
    if (!confirm('Are you sure you want to cancel this order?')) return;
    try {
      await adminCancelOrder(orderId, 'cancel');
      setOrders(
        orders.map((o) =>
          o.id === orderId
            ? { ...o, status: 'cancelled' as OrderStatus, cancellationRequested: false }
            : o
        )
      );
    } catch (error) {
      alert(error instanceof Error ? error.message : 'Failed to cancel order');
    }
  };

  const handleCancellationRequest = async (orderId: number, action: 'approve' | 'deny') => {
    try {
      await adminCancelOrder(orderId, action);
      if (action === 'approve') {
        setOrders(
          orders.map((o) =>
            o.id === orderId
              ? { ...o, status: 'cancelled' as OrderStatus, cancellationRequested: false }
              : o
          )
        );
      } else {
        // When denied, set cancellationDenied flag so user sees the message
        setOrders(
          orders.map((o) =>
            o.id === orderId ? { ...o, cancellationRequested: false, cancellationDenied: true } : o
          )
        );
      }
    } catch (error) {
      alert(error instanceof Error ? error.message : 'Failed to process request');
    }
  };

  // Count cancellation requests
  const cancellationRequests = orders.filter((o) => o.cancellationRequested);

  // Calculate weekly revenue from real orders (last 7 days)
  const getWeeklyRevenueData = () => {
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const today = new Date();
    const weekData: { name: string; revenue: number; date: Date }[] = [];

    // Create entries for last 7 days
    for (let i = 6; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(today.getDate() - i);
      date.setHours(0, 0, 0, 0);
      weekData.push({
        name: dayNames[date.getDay()],
        revenue: 0,
        date,
      });
    }

    // Sum revenue for each day from orders (exclude cancelled orders)
    orders.forEach((order) => {
      if (order.status === 'cancelled') return;
      const orderDate = new Date(order.createdAt);
      orderDate.setHours(0, 0, 0, 0);

      const dayEntry = weekData.find((d) => d.date.getTime() === orderDate.getTime());
      if (dayEntry && order.price) {
        dayEntry.revenue += order.price;
      }
    });

    return weekData.map(({ name, revenue }) => ({
      name,
      revenue: Math.round(revenue * 100) / 100,
    }));
  };

  const chartData = getWeeklyRevenueData();
  const bestDay = chartData.reduce(
    (best, day) => (day.revenue > best.revenue ? day : best),
    chartData[0]
  );

  const totalRevenue = orders
    .filter((o) => o.status !== 'cancelled')
    .reduce((sum, o) => sum + (o.price || 0), 0);

  // Show loading spinner while checking auth
  if (authLoading && !isAuthenticated) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-info animate-spin" />
      </div>
    );
  }

  // Show login form if not authenticated
  if (!isAuthenticated) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center px-4">
        <div
          className="w-full max-w-sm glass rounded-3xl p-8"
          style={{ borderColor: 'var(--border-color)', borderWidth: '1px', borderStyle: 'solid' }}
        >
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-on-gradient" />
            </div>
            <h1 className="text-2xl font-black" style={{ color: 'var(--text-primary)' }}>
              Admin Access
            </h1>
            <p className="text-sm mt-2" style={{ color: 'var(--text-secondary)' }}>
              Enter password to continue
            </p>
          </div>

          {authError && (
            <div className="mb-4 p-3 bg-red-500/20 border border-red-500/50 text-error rounded-xl text-sm">
              {authError}
            </div>
          )}

          <form onSubmit={handleLogin}>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Admin password"
              className="theme-input w-full rounded-xl px-4 py-3 mb-4"
              autoFocus
            />
            <button
              type="submit"
              disabled={authLoading || !password}
              className="w-full btn-neon disabled:opacity-50"
            >
              {authLoading ? (
                <Loader2 className="w-5 h-5 animate-spin inline mr-2" />
              ) : (
                <Lock className="w-5 h-5 inline mr-2" />
              )}
              Unlock
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto py-8 px-4">
      {/* Header */}
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-black" style={{ color: 'var(--text-primary)' }}>
            Dashboard
          </h1>
          <p style={{ color: 'var(--text-secondary)' }}>Manage your print orders</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={loadOrders}
            className="p-3 glass rounded-xl hover:bg-slate-100 dark:hover:bg-white/10 transition-colors"
            title="Refresh"
          >
            <RefreshCw
              className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`}
              style={{ color: 'var(--text-secondary)' }}
            />
          </button>
          <button
            onClick={handleLogout}
            className="p-3 glass rounded-xl hover:bg-red-500/20 transition-colors"
            title="Logout"
          >
            <LogOut className="w-5 h-5" style={{ color: 'var(--text-secondary)' }} />
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <div
          className="glass rounded-2xl p-5"
          style={{ borderColor: 'var(--border-color)', borderWidth: '1px', borderStyle: 'solid' }}
        >
          <div className="flex items-center gap-3 mb-2">
            <Package className="w-5 h-5 text-info" />
            <span className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>
              Total Orders
            </span>
          </div>
          <div className="text-3xl font-black" style={{ color: 'var(--text-primary)' }}>
            {orders.length}
          </div>
        </div>

        <div
          className="glass rounded-2xl p-5"
          style={{ borderColor: 'var(--border-color)', borderWidth: '1px', borderStyle: 'solid' }}
        >
          <div className="flex items-center gap-3 mb-2">
            <Clock className="w-5 h-5 text-warning" />
            <span className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>
              Pending
            </span>
          </div>
          <div className="text-3xl font-black text-warning">
            {orders.filter((o) => o.status === 'pending').length}
          </div>
        </div>

        <div
          className="glass rounded-2xl p-5"
          style={{ borderColor: 'var(--border-color)', borderWidth: '1px', borderStyle: 'solid' }}
        >
          <div className="flex items-center gap-3 mb-2">
            <RefreshCw className="w-5 h-5 text-info" />
            <span className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>
              Printing
            </span>
          </div>
          <div className="text-3xl font-black text-info">
            {orders.filter((o) => o.status === 'printing').length}
          </div>
        </div>

        <div
          className="glass rounded-2xl p-5"
          style={{ borderColor: 'var(--border-color)', borderWidth: '1px', borderStyle: 'solid' }}
        >
          <div className="flex items-center gap-3 mb-2">
            <TrendingUp className="w-5 h-5 text-success" />
            <span className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>
              Revenue
            </span>
          </div>
          <div className="text-3xl font-black text-success">${totalRevenue.toFixed(2)}</div>
        </div>
      </div>

      {/* Cancellation Requests Alert */}
      {cancellationRequests.length > 0 && (
        <div className="mb-6 glass rounded-2xl border border-yellow-500/30 overflow-hidden">
          <div className="p-5 border-b border-yellow-500/30 bg-yellow-500/10">
            <h2 className="text-lg font-bold text-warning flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              Cancellation Requests ({cancellationRequests.length})
            </h2>
          </div>
          <div className="p-4 space-y-3">
            {cancellationRequests.map((order) => (
              <div
                key={order.id}
                className="flex items-center justify-between p-3 rounded-xl"
                style={{ backgroundColor: 'var(--surface-inactive)' }}
              >
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-mono" style={{ color: 'var(--text-muted)' }}>
                      #{order.id}
                    </span>
                    <span className="font-medium" style={{ color: 'var(--text-primary)' }}>
                      {order.customerName}
                    </span>
                    <span
                      className={`px-2 py-0.5 rounded-full text-xs font-bold ${getStatusBadgeClass(order.status)}`}
                    >
                      {order.status.toUpperCase()}
                    </span>
                  </div>
                  {order.cancellationReason && (
                    <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>
                      Reason: "{order.cancellationReason}"
                    </p>
                  )}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleCancellationRequest(order.id, 'approve')}
                    className="px-3 py-1.5 bg-red-500/20 text-error rounded-lg text-sm font-medium hover:bg-red-500/30 transition-colors flex items-center gap-1"
                  >
                    <Check className="w-4 h-4" />
                    Approve
                  </button>
                  <button
                    onClick={() => handleCancellationRequest(order.id, 'deny')}
                    className="px-3 py-1.5 bg-slate-500/20 rounded-lg text-sm font-medium hover:bg-slate-500/30 transition-colors flex items-center gap-1"
                    style={{ color: 'var(--text-secondary)' }}
                  >
                    <X className="w-4 h-4" />
                    Deny
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Orders Table */}
        <div
          className="lg:col-span-2 glass rounded-2xl overflow-hidden"
          style={{ borderColor: 'var(--border-color)', borderWidth: '1px', borderStyle: 'solid' }}
        >
          <div
            className="p-5"
            style={{
              borderBottomColor: 'var(--border-color)',
              borderBottomWidth: '1px',
              borderBottomStyle: 'solid',
            }}
          >
            <h2
              className="text-lg font-bold flex items-center gap-2"
              style={{ color: 'var(--text-primary)' }}
            >
              <Package className="w-5 h-5 text-info" />
              Recent Orders
            </h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="table-header font-medium">
                <tr>
                  <th className="px-5 py-4">ID</th>
                  <th className="px-5 py-4">Customer</th>
                  <th className="px-5 py-4">Model</th>
                  <th className="px-5 py-4">Color</th>
                  <th className="px-5 py-4">Link</th>
                  <th className="px-5 py-4">Est.</th>
                  <th className="px-5 py-4">Final Price</th>
                  <th className="px-5 py-4">Status</th>
                  <th className="px-5 py-4">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-themed">
                {orders.map((order) => (
                  <tr
                    key={order.id}
                    className="hover:bg-slate-50 dark:hover:bg-white/5 transition-colors"
                  >
                    <td className="px-5 py-4 font-mono" style={{ color: 'var(--text-muted)' }}>
                      #{order.id}
                    </td>
                    <td className="px-5 py-4">
                      <div className="font-medium" style={{ color: 'var(--text-primary)' }}>
                        {order.customerName}
                      </div>
                      {order.email && (
                        <a
                          href={`mailto:${order.email}`}
                          className="text-xs text-info hover:opacity-80 flex items-center gap-1 mt-0.5"
                        >
                          <Mail className="w-3 h-3" />
                          {order.email}
                        </a>
                      )}
                    </td>
                    <td
                      className="px-5 py-4 truncate max-w-[150px]"
                      style={{ color: 'var(--text-secondary)' }}
                    >
                      {order.modelName}
                    </td>
                    <td className="px-5 py-4">
                      {order.colorName ? (
                        <div className="flex items-center gap-2">
                          <span
                            className="w-4 h-4 rounded-full flex-shrink-0 border border-white/20"
                            style={{ backgroundColor: order.colorHex || '#888' }}
                          />
                          <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                            {order.colorName}
                          </span>
                        </div>
                      ) : (
                        <span style={{ color: 'var(--text-muted)' }}>—</span>
                      )}
                    </td>
                    <td className="px-5 py-4">
                      {order.modelUrl && (
                        <a
                          href={order.modelUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 px-2 py-1 bg-blue-500/20 text-info rounded-lg text-xs font-medium hover:bg-blue-500/30 transition-colors"
                        >
                          <ExternalLink className="w-3 h-3" />
                          Open
                        </a>
                      )}
                    </td>
                    <td className="px-5 py-4 text-xs" style={{ color: 'var(--text-secondary)' }}>
                      {order.estimatedPrice != null ? `~$${order.estimatedPrice.toFixed(2)}` : '-'}
                    </td>
                    <td className="px-5 py-4">
                      {order.price != null ? (
                        <span className="text-success font-bold">${order.price.toFixed(2)}</span>
                      ) : editingPriceId === order.id ? (
                        <div className="flex items-center gap-1">
                          <input
                            type="number"
                            step="0.50"
                            min="0"
                            value={editingPriceValue}
                            onChange={(e) => setEditingPriceValue(e.target.value)}
                            className="theme-input w-20 rounded-lg text-xs py-1.5 px-2"
                            style={{ borderColor: 'var(--accent-blue, #3b82f6)' }}
                            placeholder="0.00"
                            autoFocus
                          />
                          <button
                            onClick={() => handleSetPrice(order.id)}
                            className="p-1 text-success hover:bg-green-500/20 rounded-lg"
                          >
                            <Check className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => {
                              setEditingPriceId(null);
                              setEditingPriceValue('');
                            }}
                            className="p-1 nav-link-inactive rounded-lg"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => {
                            setEditingPriceId(order.id);
                            setEditingPriceValue(order.estimatedPrice?.toFixed(2) || '');
                          }}
                          className="flex items-center gap-1 px-2 py-1 bg-orange-500/20 text-accent-orange rounded-lg text-xs font-medium hover:bg-orange-500/30 transition-colors"
                        >
                          <DollarSign className="w-3 h-3" />
                          Set Price
                        </button>
                      )}
                    </td>
                    <td className="px-5 py-4">
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-bold ${getStatusBadgeClass(order.status)}`}
                      >
                        {order.status.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-2">
                        <select
                          value={order.status}
                          onChange={(e) =>
                            handleStatusUpdate(order.id, e.target.value as OrderStatus)
                          }
                          className="theme-input rounded-lg text-xs py-1.5 px-2"
                        >
                          <option value="pending">Pending</option>
                          <option value="priced">Priced</option>
                          <option value="confirmed">Confirmed</option>
                          <option value="printing">Printing</option>
                          <option value="ready">Ready</option>
                          <option value="completed">Completed</option>
                          <option value="cancelled">Cancelled</option>
                        </select>
                        {order.status !== 'cancelled' && (
                          <button
                            onClick={() => handleAdminCancel(order.id)}
                            className="p-1.5 text-error hover:bg-red-500/20 rounded-lg transition-colors"
                            title="Cancel order"
                          >
                            <XCircle className="w-4 h-4" />
                          </button>
                        )}
                        {order.status === 'cancelled' && (
                          <button
                            onClick={() => handleDeleteOrder(order.id)}
                            className="p-1.5 text-error hover:bg-red-500/20 rounded-lg transition-colors"
                            title="Delete order permanently"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Chart */}
        <div
          className="glass rounded-2xl p-5"
          style={{ borderColor: 'var(--border-color)', borderWidth: '1px', borderStyle: 'solid' }}
        >
          <h2
            className="text-lg font-bold mb-6 flex items-center gap-2"
            style={{ color: 'var(--text-primary)' }}
          >
            <TrendingUp className="w-5 h-5 text-success" />
            Weekly Revenue
          </h2>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" vertical={false} />
                <XAxis
                  dataKey="name"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: '#94a3b8', fontSize: 12 }}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: '#94a3b8', fontSize: 12 }}
                  tickFormatter={(v) => `$${v}`}
                />
                <Tooltip
                  cursor={{ fill: 'rgba(255,255,255,0.05)' }}
                  contentStyle={{
                    background: '#1e293b',
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: '12px',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                  }}
                  labelStyle={{ color: '#94a3b8' }}
                  itemStyle={{ color: '#4ade80' }}
                  formatter={(value: number) => [`$${value}`, 'Revenue']}
                />
                <Bar dataKey="revenue" fill="url(#colorGradient)" radius={[6, 6, 0, 0]} />
                <defs>
                  <linearGradient id="colorGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#4ade80" />
                    <stop offset="100%" stopColor="#22c55e" />
                  </linearGradient>
                </defs>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div
            className="mt-4 pt-4"
            style={{
              borderTopColor: 'var(--border-color)',
              borderTopWidth: '1px',
              borderTopStyle: 'solid',
            }}
          >
            <div className="flex items-center justify-between text-sm">
              <span style={{ color: 'var(--text-secondary)' }}>Best day</span>
              <span className="font-bold" style={{ color: 'var(--text-primary)' }}>
                {bestDay && bestDay.revenue > 0
                  ? `${bestDay.name} ($${bestDay.revenue.toFixed(2)})`
                  : 'No revenue yet'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Spin Wheel Configuration */}
      <div
        className="mt-8 glass rounded-2xl overflow-hidden"
        style={{ borderColor: 'var(--border-color)', borderWidth: '1px', borderStyle: 'solid' }}
      >
        <button
          onClick={() => setShowSpinConfig(!showSpinConfig)}
          className="w-full p-5 flex items-center justify-between hover:bg-slate-50 dark:hover:bg-white/5 transition-colors"
        >
          <h2
            className="text-lg font-bold flex items-center gap-2"
            style={{ color: 'var(--text-primary)' }}
          >
            <Settings className="w-5 h-5 text-accent-purple" />
            Spin Wheel Configuration
          </h2>
          <span style={{ color: 'var(--text-secondary)' }}>{showSpinConfig ? '−' : '+'}</span>
        </button>

        {showSpinConfig && (
          <div
            className="p-5"
            style={{
              borderTopColor: 'var(--border-color)',
              borderTopWidth: '1px',
              borderTopStyle: 'solid',
            }}
          >
            <div className="flex justify-between items-center mb-4">
              <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                Manage spin wheel prizes and probabilities
              </p>
              <button
                onClick={() => {
                  setEditingOption({
                    id: 0,
                    label: '',
                    prizeType: 'discount_percent',
                    prizeValue: 10,
                    probability: 0.1,
                    color: '#3b82f6',
                    enabled: true,
                  });
                  setShowAddModal(true);
                }}
                className="flex items-center gap-2 px-3 py-2 bg-purple-500/20 text-accent-purple rounded-lg text-sm font-medium hover:bg-purple-500/30 transition-colors"
              >
                <Plus className="w-4 h-4" />
                Add Option
              </button>
            </div>

            {spinLoading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-6 h-6 text-accent-purple animate-spin" />
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead className="table-header font-medium">
                    <tr>
                      <th className="px-4 py-3">Color</th>
                      <th className="px-4 py-3">Label</th>
                      <th className="px-4 py-3">Type</th>
                      <th className="px-4 py-3">Value</th>
                      <th className="px-4 py-3">Probability</th>
                      <th className="px-4 py-3">Enabled</th>
                      <th className="px-4 py-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-themed">
                    {spinOptions.map((option) => (
                      <tr
                        key={option.id}
                        className="hover:bg-slate-50 dark:hover:bg-white/5 transition-colors"
                      >
                        <td className="px-4 py-3">
                          <div
                            className="w-6 h-6 rounded-full"
                            style={{
                              backgroundColor: option.color,
                              borderColor: 'var(--border-color)',
                              borderWidth: '2px',
                              borderStyle: 'solid',
                            }}
                          />
                        </td>
                        <td
                          className="px-4 py-3 font-medium"
                          style={{ color: 'var(--text-primary)' }}
                        >
                          {option.label}
                        </td>
                        <td className="px-4 py-3" style={{ color: 'var(--text-secondary)' }}>
                          {option.prizeType.replace('_', ' ')}
                        </td>
                        <td className="px-4 py-3" style={{ color: 'var(--text-secondary)' }}>
                          {option.prizeType === 'discount_percent' && `${option.prizeValue}%`}
                          {option.prizeType === 'discount_fixed' && `$${option.prizeValue}`}
                          {option.prizeType === 'free_item' && 'Free'}
                          {option.prizeType === 'nothing' && '-'}
                        </td>
                        <td className="px-4 py-3" style={{ color: 'var(--text-secondary)' }}>
                          {(option.probability * 100).toFixed(0)}%
                        </td>
                        <td className="px-4 py-3">
                          <button
                            onClick={() => handleToggleOption(option)}
                            className={`w-10 h-6 rounded-full transition-colors ${option.enabled ? 'bg-green-500' : 'bg-slate-600'}`}
                          >
                            <div
                              className={`w-4 h-4 rounded-full bg-white transition-transform mx-1 ${option.enabled ? 'translate-x-4' : ''}`}
                            />
                          </button>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex gap-2">
                            <button
                              onClick={() => {
                                setEditingOption(option);
                                setShowAddModal(true);
                              }}
                              className="p-1.5 text-info hover:bg-blue-500/20 rounded-lg transition-colors"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteOption(option.id)}
                              className="p-1.5 text-error hover:bg-red-500/20 rounded-lg transition-colors"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Filament Colors Configuration */}
      <div
        className="mt-8 glass rounded-2xl overflow-hidden"
        style={{ borderColor: 'var(--border-color)', borderWidth: '1px', borderStyle: 'solid' }}
      >
        <button
          onClick={() => setShowColorsConfig(!showColorsConfig)}
          className="w-full p-5 flex items-center justify-between hover:bg-slate-50 dark:hover:bg-white/5 transition-colors"
        >
          <h2
            className="text-lg font-bold flex items-center gap-2"
            style={{ color: 'var(--text-primary)' }}
          >
            <div className="w-5 h-5 rounded-full bg-gradient-to-r from-red-500 via-green-500 to-blue-500" />
            Filament Colors
          </h2>
          <span style={{ color: 'var(--text-secondary)' }}>{showColorsConfig ? '−' : '+'}</span>
        </button>

        {showColorsConfig && (
          <div
            className="p-5"
            style={{
              borderTopColor: 'var(--border-color)',
              borderTopWidth: '1px',
              borderTopStyle: 'solid',
            }}
          >
            <div className="flex justify-between items-center mb-4">
              <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                Manage filament color availability
              </p>
              <button
                onClick={() => {
                  setEditingColor({ id: 0, name: '', hexCode: '#3b82f6', inStock: true });
                  setShowColorModal(true);
                }}
                className="flex items-center gap-2 px-3 py-2 bg-blue-500/20 text-info rounded-lg text-sm font-medium hover:bg-blue-500/30 transition-colors"
              >
                <Plus className="w-4 h-4" />
                Add Color
              </button>
            </div>

            {colorsLoading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-6 h-6 text-info animate-spin" />
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {colors.map((color) => (
                  <div
                    key={color.id}
                    className={`flex items-center gap-3 p-3 rounded-xl border transition-colors ${
                      color.inStock
                        ? ''
                        : 'bg-red-50 dark:bg-slate-900/50 border-red-500/30 opacity-60'
                    }`}
                    style={
                      color.inStock
                        ? {
                            backgroundColor: 'var(--surface-inactive)',
                            borderColor: 'var(--border-color)',
                          }
                        : undefined
                    }
                  >
                    <div
                      className="w-10 h-10 rounded-lg flex-shrink-0"
                      style={{
                        backgroundColor: color.hexCode,
                        borderColor: 'var(--border-color)',
                        borderWidth: '2px',
                        borderStyle: 'solid',
                      }}
                    />
                    <div className="flex-1 min-w-0">
                      <div
                        className="font-medium truncate"
                        style={{ color: 'var(--text-primary)' }}
                      >
                        {color.name}
                      </div>
                      <div className="text-xs font-mono" style={{ color: 'var(--text-secondary)' }}>
                        {color.hexCode}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleToggleColorStock(color)}
                        className={`w-10 h-6 rounded-full transition-colors ${
                          color.inStock ? 'bg-green-500' : 'bg-slate-600'
                        }`}
                        title={color.inStock ? 'In Stock' : 'Out of Stock'}
                      >
                        <div
                          className={`w-4 h-4 rounded-full bg-white transition-transform mx-1 ${
                            color.inStock ? 'translate-x-4' : ''
                          }`}
                        />
                      </button>
                      <button
                        onClick={() => {
                          setEditingColor(color);
                          setShowColorModal(true);
                        }}
                        className="p-1.5 text-info hover:bg-blue-500/20 rounded-lg transition-colors"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteColor(color.id)}
                        className="p-1.5 text-error hover:bg-red-500/20 rounded-lg transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Popular Models Configuration */}
      <div
        className="mt-8 glass rounded-2xl overflow-hidden"
        style={{ borderColor: 'var(--border-color)', borderWidth: '1px', borderStyle: 'solid' }}
      >
        <button
          onClick={() => setShowPopularConfig(!showPopularConfig)}
          className="w-full p-5 flex items-center justify-between hover:bg-slate-50 dark:hover:bg-white/5 transition-colors"
        >
          <h2
            className="text-lg font-bold flex items-center gap-2"
            style={{ color: 'var(--text-primary)' }}
          >
            <Star className="w-5 h-5 text-accent-yellow" />
            Popular Models
          </h2>
          <span style={{ color: 'var(--text-secondary)' }}>{showPopularConfig ? '−' : '+'}</span>
        </button>

        {showPopularConfig && (
          <div
            className="p-5"
            style={{
              borderTopColor: 'var(--border-color)',
              borderTopWidth: '1px',
              borderTopStyle: 'solid',
            }}
          >
            <div className="flex justify-between items-center mb-4">
              <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                Manage homepage carousel models
              </p>
              <button
                onClick={() => {
                  setEditingModel({
                    id: 0,
                    name: '',
                    imageKey: '',
                    imageUrl: '',
                    modelUrl: '',
                    price: 0,
                    sortOrder: 0,
                    enabled: true,
                  });
                  setShowModelModal(true);
                }}
                className="flex items-center gap-2 px-3 py-2 bg-yellow-500/20 text-accent-yellow rounded-lg text-sm font-medium hover:bg-yellow-500/30 transition-colors"
              >
                <Plus className="w-4 h-4" />
                Add Model
              </button>
            </div>

            {popularLoading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-6 h-6 text-accent-yellow animate-spin" />
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {popularModels.map((model) => (
                  <div
                    key={model.id}
                    className={`rounded-xl border overflow-hidden transition-colors ${
                      model.enabled
                        ? ''
                        : 'bg-red-50 dark:bg-slate-900/50 border-red-500/30 opacity-60'
                    }`}
                    style={
                      model.enabled
                        ? {
                            backgroundColor: 'var(--surface-inactive)',
                            borderColor: 'var(--border-color)',
                          }
                        : undefined
                    }
                  >
                    <div
                      className="aspect-video overflow-hidden"
                      style={{ backgroundColor: 'var(--surface-inactive)' }}
                    >
                      {model.imageUrl && (
                        <img
                          src={model.imageUrl}
                          alt={model.name}
                          className="w-full h-full object-cover"
                        />
                      )}
                    </div>
                    <div className="p-3">
                      <div className="flex items-center justify-between mb-1">
                        <span
                          className="font-medium truncate"
                          style={{ color: 'var(--text-primary)' }}
                        >
                          {model.name}
                        </span>
                        <span className="text-success font-bold text-sm">
                          ${model.price.toFixed(2)}
                        </span>
                      </div>
                      <div className="text-xs mb-2" style={{ color: 'var(--text-secondary)' }}>
                        Order: {model.sortOrder}
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleToggleModelEnabled(model)}
                          className={`w-10 h-6 rounded-full transition-colors ${
                            model.enabled ? 'bg-green-500' : 'bg-slate-600'
                          }`}
                        >
                          <div
                            className={`w-4 h-4 rounded-full bg-white transition-transform mx-1 ${
                              model.enabled ? 'translate-x-4' : ''
                            }`}
                          />
                        </button>
                        <button
                          onClick={() => {
                            setEditingModel(model);
                            setShowModelModal(true);
                          }}
                          className="p-1.5 text-info hover:bg-blue-500/20 rounded-lg transition-colors"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteModel(model.id)}
                          className="p-1.5 text-error hover:bg-red-500/20 rounded-lg transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Promo Codes Configuration */}
      <div
        className="mt-8 glass rounded-2xl overflow-hidden"
        style={{ borderColor: 'var(--border-color)', borderWidth: '1px', borderStyle: 'solid' }}
      >
        <button
          onClick={() => setShowPromoConfig(!showPromoConfig)}
          className="w-full p-5 flex items-center justify-between hover:bg-slate-50 dark:hover:bg-white/5 transition-colors"
        >
          <h2
            className="text-lg font-bold flex items-center gap-2"
            style={{ color: 'var(--text-primary)' }}
          >
            <Tag className="w-5 h-5 text-accent-blue" />
            Promo Codes
          </h2>
          <span style={{ color: 'var(--text-secondary)' }}>{showPromoConfig ? '−' : '+'}</span>
        </button>

        {showPromoConfig && (
          <div
            className="p-5"
            style={{
              borderTopColor: 'var(--border-color)',
              borderTopWidth: '1px',
              borderTopStyle: 'solid',
            }}
          >
            <div className="flex justify-between items-center mb-4">
              <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                Create and manage discount codes for customers
              </p>
              <button
                onClick={() => {
                  setEditingPromo({
                    id: 0,
                    code: '',
                    type: 'percent',
                    value: 10,
                    maxUses: null,
                    uses: 0,
                    enabled: true,
                    expiresAt: null,
                    createdAt: '',
                  });
                  setShowPromoModal(true);
                }}
                className="flex items-center gap-2 px-3 py-2 bg-blue-500/20 text-info rounded-lg text-sm font-medium hover:bg-blue-500/30 transition-colors"
              >
                <Plus className="w-4 h-4" />
                Add Code
              </button>
            </div>

            {promoLoading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-6 h-6 text-info animate-spin" />
              </div>
            ) : promoCodes.length === 0 ? (
              <p className="text-center py-6 text-sm" style={{ color: 'var(--text-muted)' }}>
                No promo codes yet.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead className="table-header font-medium">
                    <tr>
                      <th className="px-4 py-3">Code</th>
                      <th className="px-4 py-3">Discount</th>
                      <th className="px-4 py-3">Uses</th>
                      <th className="px-4 py-3">Expires</th>
                      <th className="px-4 py-3">Enabled</th>
                      <th className="px-4 py-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-themed">
                    {promoCodes.map((promo) => (
                      <tr
                        key={promo.id}
                        className="hover:bg-slate-50 dark:hover:bg-white/5 transition-colors"
                      >
                        <td
                          className="px-4 py-3 font-mono font-bold"
                          style={{ color: 'var(--text-primary)' }}
                        >
                          {promo.code}
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-success font-bold">
                            {promo.type === 'percent'
                              ? `${promo.value}%`
                              : `$${promo.value.toFixed(2)}`}{' '}
                            off
                          </span>
                        </td>
                        <td className="px-4 py-3" style={{ color: 'var(--text-secondary)' }}>
                          {promo.uses}
                          {promo.maxUses !== null ? ` / ${promo.maxUses}` : ''}
                        </td>
                        <td className="px-4 py-3" style={{ color: 'var(--text-secondary)' }}>
                          {promo.expiresAt
                            ? new Date(promo.expiresAt).toLocaleDateString()
                            : 'Never'}
                        </td>
                        <td className="px-4 py-3">
                          <button
                            onClick={() => handleTogglePromo(promo)}
                            className={`w-10 h-6 rounded-full transition-colors ${promo.enabled ? 'bg-green-500' : 'bg-slate-600'}`}
                          >
                            <div
                              className={`w-4 h-4 rounded-full bg-white transition-transform mx-1 ${promo.enabled ? 'translate-x-4' : ''}`}
                            />
                          </button>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex gap-2">
                            <button
                              onClick={() => {
                                setEditingPromo(promo);
                                setShowPromoModal(true);
                              }}
                              className="p-1.5 text-info hover:bg-blue-500/20 rounded-lg transition-colors"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeletePromo(promo.id)}
                              className="p-1.5 text-error hover:bg-red-500/20 rounded-lg transition-colors"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Color Edit/Add Modal */}
      {showColorModal && editingColor && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div
            className="glass rounded-2xl p-6 w-full max-w-md"
            style={{ borderColor: 'var(--border-color)', borderWidth: '1px', borderStyle: 'solid' }}
          >
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>
                {editingColor.id ? 'Edit Color' : 'Add Color'}
              </h3>
              <button
                onClick={() => {
                  setShowColorModal(false);
                  setEditingColor(null);
                }}
                className="nav-link-inactive"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label
                  className="block text-sm font-medium mb-1"
                  style={{ color: 'var(--text-secondary)' }}
                >
                  Color Name
                </label>
                <input
                  type="text"
                  value={editingColor.name}
                  onChange={(e) => setEditingColor({ ...editingColor, name: e.target.value })}
                  className="theme-input w-full rounded-xl px-4 py-2"
                  placeholder="e.g., Ocean Blue"
                />
              </div>

              <div>
                <label
                  className="block text-sm font-medium mb-1"
                  style={{ color: 'var(--text-secondary)' }}
                >
                  Hex Code
                </label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={editingColor.hexCode}
                    onChange={(e) => setEditingColor({ ...editingColor, hexCode: e.target.value })}
                    className="w-12 h-10 rounded-lg cursor-pointer"
                  />
                  <input
                    type="text"
                    value={editingColor.hexCode}
                    onChange={(e) => setEditingColor({ ...editingColor, hexCode: e.target.value })}
                    className="theme-input flex-1 rounded-xl px-4 py-2 font-mono"
                    placeholder="#RRGGBB"
                  />
                </div>
              </div>

              <div className="flex items-center gap-3">
                <button
                  onClick={() =>
                    setEditingColor({ ...editingColor, inStock: !editingColor.inStock })
                  }
                  className={`w-10 h-6 rounded-full transition-colors ${
                    editingColor.inStock ? 'bg-green-500' : 'bg-slate-600'
                  }`}
                >
                  <div
                    className={`w-4 h-4 rounded-full bg-white transition-transform mx-1 ${
                      editingColor.inStock ? 'translate-x-4' : ''
                    }`}
                  />
                </button>
                <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                  In Stock
                </span>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setShowColorModal(false);
                  setEditingColor(null);
                }}
                className="flex-1 px-4 py-2 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                style={{
                  borderColor: 'var(--border-color)',
                  borderWidth: '1px',
                  borderStyle: 'solid',
                  color: 'var(--text-secondary)',
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => handleSaveColor(editingColor)}
                disabled={!editingColor.name || !editingColor.hexCode}
                className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-xl hover:bg-blue-600 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
              >
                <Check className="w-4 h-4" />
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit/Add Modal */}
      {showAddModal && editingOption && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div
            className="glass rounded-2xl p-6 w-full max-w-md"
            style={{ borderColor: 'var(--border-color)', borderWidth: '1px', borderStyle: 'solid' }}
          >
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>
                {editingOption.id ? 'Edit Spin Option' : 'Add Spin Option'}
              </h3>
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setEditingOption(null);
                }}
                className="nav-link-inactive"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label
                  className="block text-sm font-medium mb-1"
                  style={{ color: 'var(--text-secondary)' }}
                >
                  Label
                </label>
                <input
                  type="text"
                  value={editingOption.label}
                  onChange={(e) => setEditingOption({ ...editingOption, label: e.target.value })}
                  className="theme-input w-full rounded-xl px-4 py-2"
                  placeholder="e.g., 10% Off"
                />
              </div>

              <div>
                <label
                  className="block text-sm font-medium mb-1"
                  style={{ color: 'var(--text-secondary)' }}
                >
                  Prize Type
                </label>
                <select
                  value={editingOption.prizeType}
                  onChange={(e) =>
                    setEditingOption({ ...editingOption, prizeType: e.target.value })
                  }
                  className="theme-input w-full rounded-xl px-4 py-2"
                >
                  <option value="discount_percent">Discount %</option>
                  <option value="discount_fixed">Discount $</option>
                  <option value="free_item">Free Item</option>
                  <option value="nothing">Nothing (Try Again)</option>
                </select>
              </div>

              <div>
                <label
                  className="block text-sm font-medium mb-1"
                  style={{ color: 'var(--text-secondary)' }}
                >
                  Prize Value
                </label>
                <input
                  type="number"
                  value={editingOption.prizeValue}
                  onChange={(e) =>
                    setEditingOption({
                      ...editingOption,
                      prizeValue: Number.parseFloat(e.target.value) || 0,
                    })
                  }
                  className="theme-input w-full rounded-xl px-4 py-2"
                  placeholder="e.g., 10"
                />
              </div>

              <div>
                <label
                  className="block text-sm font-medium mb-1"
                  style={{ color: 'var(--text-secondary)' }}
                >
                  Probability (0-1)
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max="1"
                  value={editingOption.probability}
                  onChange={(e) =>
                    setEditingOption({
                      ...editingOption,
                      probability: Number.parseFloat(e.target.value) || 0,
                    })
                  }
                  className="theme-input w-full rounded-xl px-4 py-2"
                  placeholder="e.g., 0.25"
                />
              </div>

              <div>
                <label
                  className="block text-sm font-medium mb-1"
                  style={{ color: 'var(--text-secondary)' }}
                >
                  Color
                </label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={editingOption.color}
                    onChange={(e) => setEditingOption({ ...editingOption, color: e.target.value })}
                    className="w-12 h-10 rounded-lg cursor-pointer"
                  />
                  <input
                    type="text"
                    value={editingOption.color}
                    onChange={(e) => setEditingOption({ ...editingOption, color: e.target.value })}
                    className="theme-input flex-1 rounded-xl px-4 py-2"
                  />
                </div>
              </div>

              <div className="flex items-center gap-3">
                <button
                  onClick={() =>
                    setEditingOption({ ...editingOption, enabled: !editingOption.enabled })
                  }
                  className={`w-10 h-6 rounded-full transition-colors ${editingOption.enabled ? 'bg-green-500' : 'bg-slate-600'}`}
                >
                  <div
                    className={`w-4 h-4 rounded-full bg-white transition-transform mx-1 ${editingOption.enabled ? 'translate-x-4' : ''}`}
                  />
                </button>
                <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                  Enabled
                </span>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setEditingOption(null);
                }}
                className="flex-1 px-4 py-2 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                style={{
                  borderColor: 'var(--border-color)',
                  borderWidth: '1px',
                  borderStyle: 'solid',
                  color: 'var(--text-secondary)',
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => handleSaveOption(editingOption)}
                className="flex-1 px-4 py-2 bg-purple-500 text-white rounded-xl hover:bg-purple-600 transition-colors flex items-center justify-center gap-2"
              >
                <Check className="w-4 h-4" />
                Save
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Promo Code Edit/Add Modal */}
      {showPromoModal && editingPromo && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div
            className="glass rounded-2xl p-6 w-full max-w-md"
            style={{ borderColor: 'var(--border-color)', borderWidth: '1px', borderStyle: 'solid' }}
          >
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>
                {editingPromo.id ? 'Edit Promo Code' : 'New Promo Code'}
              </h3>
              <button
                onClick={() => {
                  setShowPromoModal(false);
                  setEditingPromo(null);
                }}
                className="nav-link-inactive"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label
                  className="block text-sm font-medium mb-1"
                  style={{ color: 'var(--text-secondary)' }}
                >
                  Code
                </label>
                <input
                  type="text"
                  value={editingPromo.code}
                  onChange={(e) =>
                    setEditingPromo({ ...editingPromo, code: e.target.value.toUpperCase() })
                  }
                  className="theme-input w-full rounded-xl px-4 py-2 font-mono uppercase"
                  placeholder="SUMMER10"
                />
              </div>

              <div>
                <label
                  className="block text-sm font-medium mb-1"
                  style={{ color: 'var(--text-secondary)' }}
                >
                  Discount Type
                </label>
                <select
                  value={editingPromo.type}
                  onChange={(e) =>
                    setEditingPromo({
                      ...editingPromo,
                      type: e.target.value as 'percent' | 'fixed',
                    })
                  }
                  className="theme-input w-full rounded-xl px-4 py-2"
                >
                  <option value="percent">Percent off (%)</option>
                  <option value="fixed">Fixed amount ($)</option>
                </select>
              </div>

              <div>
                <label
                  className="block text-sm font-medium mb-1"
                  style={{ color: 'var(--text-secondary)' }}
                >
                  {editingPromo.type === 'percent' ? 'Percent Off (%)' : 'Amount Off ($)'}
                </label>
                <input
                  type="number"
                  step={editingPromo.type === 'percent' ? '1' : '0.50'}
                  min="0"
                  max={editingPromo.type === 'percent' ? '100' : undefined}
                  value={editingPromo.value}
                  onChange={(e) =>
                    setEditingPromo({
                      ...editingPromo,
                      value: Number.parseFloat(e.target.value) || 0,
                    })
                  }
                  className="theme-input w-full rounded-xl px-4 py-2"
                  placeholder={editingPromo.type === 'percent' ? '10' : '1.00'}
                />
              </div>

              <div>
                <label
                  className="block text-sm font-medium mb-1"
                  style={{ color: 'var(--text-secondary)' }}
                >
                  Max Uses{' '}
                  <span className="font-normal opacity-60">(leave blank for unlimited)</span>
                </label>
                <input
                  type="number"
                  min="1"
                  value={editingPromo.maxUses ?? ''}
                  onChange={(e) =>
                    setEditingPromo({
                      ...editingPromo,
                      maxUses: e.target.value ? Number.parseInt(e.target.value, 10) : null,
                    })
                  }
                  className="theme-input w-full rounded-xl px-4 py-2"
                  placeholder="Unlimited"
                />
              </div>

              <div>
                <label
                  className="block text-sm font-medium mb-1"
                  style={{ color: 'var(--text-secondary)' }}
                >
                  Expiry Date{' '}
                  <span className="font-normal opacity-60">(leave blank for no expiry)</span>
                </label>
                <input
                  type="datetime-local"
                  value={editingPromo.expiresAt ? editingPromo.expiresAt.slice(0, 16) : ''}
                  onChange={(e) =>
                    setEditingPromo({
                      ...editingPromo,
                      expiresAt: e.target.value ? `${e.target.value}:00` : null,
                    })
                  }
                  className="theme-input w-full rounded-xl px-4 py-2"
                />
              </div>

              <div className="flex items-center gap-3">
                <button
                  onClick={() =>
                    setEditingPromo({ ...editingPromo, enabled: !editingPromo.enabled })
                  }
                  className={`w-10 h-6 rounded-full transition-colors ${editingPromo.enabled ? 'bg-green-500' : 'bg-slate-600'}`}
                >
                  <div
                    className={`w-4 h-4 rounded-full bg-white transition-transform mx-1 ${editingPromo.enabled ? 'translate-x-4' : ''}`}
                  />
                </button>
                <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                  Enabled
                </span>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setShowPromoModal(false);
                  setEditingPromo(null);
                }}
                className="flex-1 px-4 py-2 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                style={{
                  borderColor: 'var(--border-color)',
                  borderWidth: '1px',
                  borderStyle: 'solid',
                  color: 'var(--text-secondary)',
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => handleSavePromo(editingPromo)}
                disabled={!editingPromo.code || editingPromo.value <= 0}
                className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-xl hover:bg-blue-600 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
              >
                <Check className="w-4 h-4" />
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Popular Model Edit/Add Modal */}
      {showModelModal && editingModel && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div
            className="glass rounded-2xl p-6 w-full max-w-md"
            style={{ borderColor: 'var(--border-color)', borderWidth: '1px', borderStyle: 'solid' }}
          >
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>
                {editingModel.id ? 'Edit Model' : 'Add Model'}
              </h3>
              <button
                onClick={() => {
                  setShowModelModal(false);
                  setEditingModel(null);
                }}
                className="nav-link-inactive"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label
                  className="block text-sm font-medium mb-1"
                  style={{ color: 'var(--text-secondary)' }}
                >
                  Model Name
                </label>
                <input
                  type="text"
                  value={editingModel.name}
                  onChange={(e) => setEditingModel({ ...editingModel, name: e.target.value })}
                  className="theme-input w-full rounded-xl px-4 py-2"
                  placeholder="e.g., Benchy"
                />
              </div>

              <div>
                <label
                  className="block text-sm font-medium mb-1"
                  style={{ color: 'var(--text-secondary)' }}
                >
                  Model URL (MakerWorld / Printables)
                </label>
                <input
                  type="url"
                  value={editingModel.modelUrl}
                  onChange={(e) => setEditingModel({ ...editingModel, modelUrl: e.target.value })}
                  className="theme-input w-full rounded-xl px-4 py-2"
                  placeholder="https://makerworld.com/en/models/..."
                />
              </div>

              <div>
                <label
                  className="block text-sm font-medium mb-1"
                  style={{ color: 'var(--text-secondary)' }}
                >
                  Image
                </label>
                {editingModel.imageUrl && (
                  <img
                    src={editingModel.imageUrl}
                    alt="Preview"
                    className="w-full h-32 object-cover rounded-xl mb-2"
                  />
                )}
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    const key = await handleUploadImage(file);
                    if (key) {
                      setEditingModel({
                        ...editingModel,
                        imageKey: key,
                        imageUrl: `/api/images/${key.replace('models/', '')}`,
                      });
                    }
                  }}
                  className="w-full text-sm file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-yellow-500/20 file:text-yellow-400 hover:file:bg-yellow-500/30"
                  style={{ color: 'var(--text-secondary)' }}
                />
                {uploadingImage && (
                  <div
                    className="flex items-center gap-2 mt-2 text-sm"
                    style={{ color: 'var(--text-secondary)' }}
                  >
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Uploading...
                  </div>
                )}
              </div>

              <div>
                <label
                  className="block text-sm font-medium mb-1"
                  style={{ color: 'var(--text-secondary)' }}
                >
                  Price ($)
                </label>
                <input
                  type="number"
                  step="0.50"
                  min="0"
                  value={editingModel.price}
                  onChange={(e) =>
                    setEditingModel({
                      ...editingModel,
                      price: Number.parseFloat(e.target.value) || 0,
                    })
                  }
                  className="theme-input w-full rounded-xl px-4 py-2"
                  placeholder="3.50"
                />
              </div>

              <div>
                <label
                  className="block text-sm font-medium mb-1"
                  style={{ color: 'var(--text-secondary)' }}
                >
                  Sort Order
                </label>
                <input
                  type="number"
                  min="0"
                  value={editingModel.sortOrder}
                  onChange={(e) =>
                    setEditingModel({
                      ...editingModel,
                      sortOrder: Number.parseInt(e.target.value, 10) || 0,
                    })
                  }
                  className="theme-input w-full rounded-xl px-4 py-2"
                  placeholder="0"
                />
              </div>

              <div className="flex items-center gap-3">
                <button
                  onClick={() =>
                    setEditingModel({ ...editingModel, enabled: !editingModel.enabled })
                  }
                  className={`w-10 h-6 rounded-full transition-colors ${
                    editingModel.enabled ? 'bg-green-500' : 'bg-slate-600'
                  }`}
                >
                  <div
                    className={`w-4 h-4 rounded-full bg-white transition-transform mx-1 ${
                      editingModel.enabled ? 'translate-x-4' : ''
                    }`}
                  />
                </button>
                <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                  Enabled
                </span>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setShowModelModal(false);
                  setEditingModel(null);
                }}
                className="flex-1 px-4 py-2 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                style={{
                  borderColor: 'var(--border-color)',
                  borderWidth: '1px',
                  borderStyle: 'solid',
                  color: 'var(--text-secondary)',
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => handleSaveModel(editingModel)}
                disabled={!editingModel.name || !editingModel.imageKey || !editingModel.price}
                className="flex-1 px-4 py-2 bg-yellow-500 rounded-xl hover:bg-yellow-600 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                style={{ color: '#0f172a' }}
              >
                <Check className="w-4 h-4" />
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
